
import React, { useEffect, useState } from "react";
import { Card, Table, Spinner, Form, Button, Modal, Row, Col } from "react-bootstrap";
import { fetchOrder, updateOrderStatus } from "../../service/OrderService";
import { fetchCustomer } from "../../service/CustomerService";
import { useToast } from "../toast/ToastProvider";
import { formatINR } from "../../App";
import Main from "./Main";
import PageContainer from "./PageContainer";
import { Eye } from "react-feather";
import { apiUrl } from "../../config";
import { fetchCustomerAddressByCustId } from "../../service/CustomerAddressServices";
import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable";
import { FaFileDownload } from "react-icons/fa";

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [customerAddress, setCustomerAddress] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const toast = useToast();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const orderRes = await fetchOrder();
      const customerRes = await fetchCustomer();
      setOrders(orderRes || []);
      setCustomers(customerRes || []);
    } catch (err) {
      toast("Failed to load orders", "ERROR");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const customerAddress = async () => {
      if (selectedOrder?.customerId) {
        try {
          const address = await fetchCustomerAddressByCustId(selectedOrder.customerId);
          if (address && address.length > 0) {
            const { address: addr, city, state, zipCode } = address[0];
            setCustomerAddress({
              address: addr || "N/A",
              city: city || "N/A",
              state: state || "N/A",
              zipCode: zipCode || "N/A",
            });

          } else {
            setCustomerAddress({
              address: "Not available",
              city: "Not available",
              state: "Not available",
              zipCode: "Not available",
            });
          }
        } catch (error) {
          console.error("error fetching Address", "ERROR");

        }
      }
    }
    customerAddress();

  }, [selectedOrder])

  const getCustomerName = (id) => {
    const customer = customers.find((c) => c.id === id);
    return customer ? customer.name : "N/A";
  };

  const getCustomerPhone = (id) => {
    const customer = customers.find((c) => c.id === id);
    return customer ? customer.phone : "N/A";
  };


  const handleStatusChange = async (orderId, status) => {
    setUpdatingOrderId(orderId);
    console.log("orderId", orderId);

    const StatusDate = {
      ordered: "orderedAt",
      shipped: "shippedAt",
      packed: "packedAt",
      delivered: "deliveredAt",
    }
    const currentDate = new Date().toISOString().slice(0, 19).replace("T", " ");
    const payload = {
      id: orderId,
      status: status,
      [StatusDate[status]]: currentDate
    };

    try {
      await updateOrderStatus(payload);
      // Optimistic UI update
      setOrders((prev) =>
        prev.map((order) =>
          order.orderId === orderId
            ? {
              ...order,
              status: status,
              [StatusDate[status]]: currentDate,
            }
            : order
        )
      );
      loadOrders();
    } catch (err) {
      console.error("Status update failed:", err);
      toast("Failed to update status", "ERROR");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge bg-light text-dark">Unknown</span>;
    const variants = {
      ordered: "secondary",
      packed: "warning",
      shipped: "info",
      delivered: "success",
    };
    return <span className={`badge bg-${variants[status]}`}>{status}</span>;
  };

  const statusOptions = [
    { value: "ordered", label: "Ordered" },
    { value: "packed", label: "Packed" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

const generatePdf = () => {
  if (!selectedOrder) return;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();

  const title = `Order Details - ID: ${selectedOrder.orderId}`;
  const titleWidth = doc.getTextWidth(title);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, (pageWidth - titleWidth) / 2, 15);

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Customer Details:", 14, 25);
  doc.setFont("helvetica", "normal");

  const leftStartY = 30;
  const leftX = 16;
  const leftDetails = [
    `Name: ${getCustomerName(selectedOrder.customerId)}`,
    `Phone: ${getCustomerPhone(selectedOrder.customerId)}`,
    `Address: ${customerAddress.address}`,
    `City: ${customerAddress.city}`,
    `State: ${customerAddress.state}`,
    `Pincode: ${customerAddress.zipCode}`,
  ];
  const lineHeight = 7;

  leftDetails.forEach((line, i) => {
    doc.text(line, leftX, leftStartY + i * lineHeight);
  });

  doc.setFont("helvetica", "bold");
  doc.text("Order Summary:", pageWidth / 2 + 10, 25);
  doc.setFont("helvetica", "normal");

  const rightStartX = pageWidth / 2 + 12;
  const rightStartY = 30;
  const rightDetails = [
    [`Total Quantity:`,String(selectedOrder.totalQuantity)],
    [`Total Amount:`,` ${selectedOrder.orderTotalAmount?.toLocaleString()}`],
    [
      `Ordered On:`,
      new Date(selectedOrder.orderedAt).toLocaleString(),
    ],
  ];

  rightDetails.forEach(([label, value], i) => {
    doc.text(label, rightStartX, rightStartY + i * 6);
    doc.text(value, rightStartX + 40, rightStartY + i * 6);
  });

  // Draw a horizontal separator line
  const sectionEndY = Math.max(
    leftStartY + leftDetails.length * 6,
    rightStartY + rightDetails.length * 6
  );
  doc.line(10, sectionEndY + 5, pageWidth - 10, sectionEndY + 5);

  // ======= Order Items Table =======
  const orderItems = orders.filter(
    (od) => od.orderId === selectedOrder.orderId
  );

  const tableHeaders = [
    "S.No",
    "Product Name",
    "Quantity",
    "Status",
    "Amount",
  ];

  const tableBody = orderItems.map((item, i) => [
    i + 1,
    `${item.productName} (${item.productCode})`,
    item.productQuantity,
    item.status,
    item.itemTotalAmount?.toLocaleString(),
  ]);

  autoTable(doc, {
    head: [tableHeaders],
    body: tableBody,
    startY: sectionEndY + 10,
    theme: "grid",
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      fontStyle: "bold",
    },
    styles: {
      fontSize: 9,
      cellPadding: 2,
    },
  });

  // ======= Product Details =======
  let finalY = (doc ).lastAutoTable.finalY + 10;
  doc.setFont("helvetica", "bold");
  doc.text("Product Details:", 14, finalY);
  doc.setFont("helvetica", "normal");

  const productDetails = [
    ["Product Name:", `${selectedOrder.productName} (${selectedOrder.productCode})`],
    ["Rate:", ` ${selectedOrder.productRate}`],
    ["Weight:", `${selectedOrder.weight} g`],
    ["Gold Rate:", ` ${selectedOrder.goldRate?.toLocaleString()}`],
    ["Wastage Charge:", ` ${selectedOrder.wastageCharge?.toLocaleString()}`],
    ["Making Charge:", ` ${selectedOrder.makingCharge?.toLocaleString()}`],
    ["Quantity:", `${selectedOrder.productQuantity}`],
  ];

  autoTable(doc, {
    startY: finalY + 4,
    body: productDetails,
    theme: "plain",
    styles: {
      fontSize: 10,
      cellPadding: 2,
    },
    columnStyles: {
      0: { fontStyle: "bold" },
    },
  });

  // ======= Footer =======
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(
    `Generated on ${new Date().toLocaleString()}`,
    14,
    pageHeight - 10
  );

  doc.save(`Order_${selectedOrder.orderId}.pdf`);
};

  return (
    <Main>
      <PageContainer title="Order Management">
        <div className="container py-5">
          <Card className="shadow-sm border-0">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>S.No</th>
                      <th>ProductName</th>
                      <th>Weight</th>
                      <th>Quantity</th>
                      <th>Total Amount</th>
                      <th>Customer</th>
                      <th>OrderId</th>
                      <th>Status</th>
                      <th style={{ width: "20%" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-4">
                          No orders found
                        </td>
                      </tr>
                    ) : (
                      orders.map((order, index) => (

                        <tr key={order.orderId}>
                          <td>{index + 1}</td>
                          <td style={{ textTransform: 'capitalize' }}>{order.productName}  ({order.productCode})</td>
                          <td style={{ textAlign:'center' }}>{order.weight}</td>
                          <td style={{ textAlign: 'center' }}>{order.productQuantity}</td>
                          <td>{formatINR(Math.round(order.orderTotalAmount))}</td>
                          <td style={{ textTransform: 'capitalize' }}>{getCustomerName(order.customerId)}  {getCustomerPhone(order.customerId)}</td>
                          <td style={{ textAlign: 'center' }}>{order.orderId}</td>
                          <td>{getStatusBadge(order.status)}
                            <p><small>
                              {order.status === 'ordered' && order.orderedAt
                                ? new Date(order.orderedAt).toLocaleDateString()
                                : order.status === 'packed' && order.packedAt
                                  ? new Date(order.packedAt).toLocaleDateString()
                                  : order.status === 'shipped' && order.shippedAt
                                    ? new Date(order.shippedAt).toLocaleDateString()
                                    : order.status === 'delivered' && order.deliveredAt
                                      ? new Date(order.deliveredAt).toLocaleDateString()
                                      : '-'}
                            </small></p>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <Form.Select
                                size="sm"
                                value={order.status || "ordered"}
                                onChange={(e) =>
                                  handleStatusChange(order.itemId, e.target.value)
                                }
                                disabled={updatingOrderId === order.orderId}
                                className="me-2"
                                style={{ maxWidth: "140px" }}
                              >
                                {statusOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Form.Select>

                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setSelectedOrder(order)}
                              >
                                <Eye size={16} />
                              </Button>

                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* 🔹 Modal for Viewing Order Details */}
        <Modal
          show={!!selectedOrder}
          onHide={() => setSelectedOrder(null)}
          centered
          size="lg"
        >{selectedOrder && (
          <>
              <Modal.Header closeButton>
                <div className="d-flex justify-content-between align-items-center w-100">
                  <Modal.Title>
                    Order ID: {selectedOrder.orderId}
                  </Modal.Title>
                
                </div>
              </Modal.Header>
              <Modal.Body>
                <div className="d-flex justify-content-between bg-light rounded mb-4 p-4">
                  <div>
                    <p style={{ textTransform: 'capitalize' }}><strong>Customer Name: </strong>{getCustomerName(selectedOrder.customerId)}</p>
                    <p><strong>Phone:</strong> {getCustomerPhone(selectedOrder.customerId)}</p>
                    <p><strong>Address:</strong>{customerAddress.address}</p>
                    <p style={{ textTransform: 'capitalize' }}><strong>City:</strong>{customerAddress.city}</p>
                    <p style={{ textTransform: 'capitalize' }}><strong>State:</strong>{customerAddress.state}</p>
                    <p><strong>Pincode:</strong>{customerAddress.zipCode}</p>
                  </div>
                  <div>
                    <p>
                      <strong>Total Quantity:</strong> {selectedOrder.totalQuantity}
                    </p>
                    <p>
                      <strong>Total Amount:</strong> {formatINR(Math.round(selectedOrder.orderTotalAmount))}
                    </p>
                    <p>
                      <strong>Ordered On:</strong>{" "}
                      {new Date(selectedOrder.orderedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
                <hr />
                <h6>Order Details</h6>
                <Table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead style={{ background: '#f5f5f5' }}>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>S.no</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>ProductImage</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>productName</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>Weight(gm)</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>Quantity</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>status</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.filter((od) => od.orderId === selectedOrder.orderId).map((item, i) => (
                      <tr key={item.itemId}>
                        <td>{i + 1}</td>
                        <td>
                          <img src={item.productImageUrl ? `${apiUrl}/${item.productImageUrl.replace(/\\/g, '/')}` : '/imgages/placedholder-product.jpg'}
                            alt={item.productImageUrl}
                            width={50}
                            rounded />
                        </td>
                        <td style={{ textTransform: 'capitalize' }}>{item.productName} <p>({item.productCode})</p></td>
                        <td style={{ textAlign:'center' }}>{item.weight}gm</td>
                        <td style={{ textAlign: 'center' }}>{item.productQuantity}</td>
                        <td>{getStatusBadge(item.status)}
                          <p><small>{item.orderedAt ?
                            new Date(item.orderedAt).toLocaleDateString() :
                            item.packedAt ? new Date(item.packedAt).toLocaleDateString() : item.shippedAt ? new Date(item.shippedAt).toLocaleDateString() : item.deliveredAt ? new Date(item.deliveredAt).toLocaleDateString() : '-'}</small></p>
                        </td>
                        <td>{formatINR(Math.round(item.orderTotalAmount))}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {/* <h6>Product Details</h6> */}
                {/* <Card>
                  <Row className="px-3">
                    <Col md={5} className="d-flex justify-content-center align-items-center" >
                      <img src={selectedOrder.productImageUrl ? `${apiUrl}/${selectedOrder.productImageUrl.replace(/\\/g, '/')}` : '/images/placeholder-product.jpg'}
                        alt={selectedOrder.productName}
                        fluid
                        rounded
                        style={{ maxHeight: "200px", objectFit: "cover" }} />
                    </Col>
                    <Col md={7}>
                      <h4 className="fw-bold text-primary mb-3">{selectedOrder.productName}  ({selectedOrder.productCode})</h4>

                      <Table bordered hover responsive className="mb-2">
                        <tbody>
                          <tr>
                            <th>Rate</th>
                            <td>₹ {selectedOrder.productRate}</td>
                          </tr>
                          <tr>
                            <th>Weight</th>
                            <td>{selectedOrder.weight} g</td>
                          </tr>
                          <tr>
                            <th>Gold Rate</th>
                            <td>₹ {selectedOrder.goldRate?.toLocaleString()}</td>
                          </tr>
                          <tr>
                            <th>Wastage Charge</th>
                            <td>₹ {selectedOrder.wastageCharge?.toLocaleString()}</td>
                          </tr>
                          <tr>
                            <th>Making Charge</th>
                            <td>₹ {selectedOrder.makingCharge?.toLocaleString()}</td>
                          </tr>
                          <tr>
                            <th>Quantity</th>
                            <td>{selectedOrder.productQuantity}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </Card> */}
              </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="success"
                    size="sm"
                    onClick={generatePdf}
                  >
                    <FaFileDownload />
                  </Button>

              <Button variant="secondary" onClick={() => setSelectedOrder(null)}>
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
        </Modal>
      </PageContainer>
    </Main>
  );
};

export default OrderManagement;